import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { css } from '@emotion/react';
import { EditorState, SelectionState, Modifier } from 'draft-js';

import EditorUtils from '../../../draft-js-plugins-utils/src';
import { DeleteIcon } from '../../../../../../../images/Icons';
import URLUtils from '$/shared/frontend/utils/urls/URLUtils';
import { EditorTheme } from '../../../../EditorTheme';
import { defaultTheme } from '../theme';
import {
  InputErrorMessage,
  requiredMessage,
  InvalidUrlMessage,
} from '../../../draft-js-cta-plugin/components/CTAForm';

export const updateLinkAtSelection = (editorState, url) => {
  let entityKey = EditorUtils.getCurrentEntityKey(editorState);
  const contentState = editorState.getCurrentContent();
  if (!entityKey) {
    entityKey = contentState.getLastCreatedEntityKey(editorState);
  }
  const newContentState = contentState.mergeEntityData(entityKey, { url: url });
  const newEditorState = EditorState.push(editorState, newContentState, 'apply-entity');

  const selection = editorState.getSelection();
  const anchorKey = selection.getAnchorKey();
  const anchorBlock = contentState.getBlockForKey(anchorKey);

  return EditorState.forceSelection(
    newEditorState,
    selection.merge({
      anchorKey: anchorKey,
      focusKey: anchorKey,
      anchorOffset: anchorBlock.getText().length,
      focusOffset: anchorBlock.getText().length,
    })
  );
};

export const RemoveBlock = (props) => {
  const onRemoveLink = (e) => {
    e.stopPropagation();
    e.preventDefault();

    const { getEditorState, setEditorState } = props;

    if (!props.isEntireBlock && EditorUtils.hasEntity(getEditorState(), 'IMAGE')) {
      setEditorState(updateLinkAtSelection(getEditorState(), ''));
      props.onOverrideContent(undefined);
      return;
    }

    const editorState = getEditorState();
    const selection = editorState.getSelection();

    const content = editorState.getCurrentContent();
    const startKey = selection.getStartKey();
    const startOffset = selection.getStartOffset();
    const block = content.getBlockForKey(startKey);
    const linkKey = block.getEntityAt(startOffset);

    let contentWithRemovedLink = content;
    block.findEntityRanges(
      (charData) => {
        const entityKey = charData.getEntity();
        if (!entityKey) return false;
        return entityKey === linkKey;
      },
      (start, end) => {
        const entitySelection = new SelectionState({
          anchorKey: block.getKey(),
          focusKey: block.getKey(),
          anchorOffset: start,
          focusOffset: end,
        });
        contentWithRemovedLink = Modifier.applyEntity(content, entitySelection, null);
        return;
      }
    );

    const newEditorState = EditorState.set(editorState, {
      currentContent: contentWithRemovedLink,
    });
    setEditorState(newEditorState);

    props.onOverrideContent(undefined);
  };
  const buttonWrapper = props?.title ? EditorTheme.buttonStyles.buttonGroup : undefined;
  return (
    <div css={buttonWrapper}>
      <button
        onClick={onRemoveLink}
        css={[
          css`
            margin-left: 0.125rem;
          `,
          EditorTheme.buttonStyles.button,
        ]}
      >
        <DeleteIcon />
      </button>
      {props?.title && <span>{props.title}</span>}
    </div>
  );
};

const AddLinkForm = (props) => {
  const [value, setValue] = useState(props.value);
  const [isInvalid, setIsvalid] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const input = useRef();

  useEffect(() => {
    if (input && input.current) {
      input.current.focus();
    }
  }, []);

  React.useEffect(() => {
    setValue(props.value);
    if (input && input.current) {
      input.current.value = props.value || '';
    }
  }, [props.value]);

  const onRef = (node) => {
    input.current = node;
  };

  const isUrl = (urlValue) => {
    if (props.validateUrl) {
      return props.validateUrl(urlValue);
    }

    return URLUtils.isUrl(urlValue);
  };

  const onMouseDown = (event) => {
    event.preventDefault();
  };

  const onClose = () => props.onOverrideContent(undefined);

  const handleRequiredUrl = () => {
    setIsvalid(true);
    setErrorMessage(requiredMessage);
  };

  const handleInvalidUrl = () => {
    setIsvalid(true);
    setErrorMessage(InvalidUrlMessage);
  };

  const onChange = (e) => {
    const newValue = e.target.value;
    if (newValue !== '' && !URLUtils.isMail(URLUtils.normaliseMail(newValue))) {
      if (!isUrl(URLUtils.normalizeUrl(newValue))) {
        handleInvalidUrl();
      } else {
        setIsvalid(false);
      }
    } else {
      setIsvalid(false);
    }

    setValue(newValue);
  };

  const submit = async () => {
    const { getEditorState, setEditorState } = props;
    let url = value || '';
    if (url === '') {
      handleRequiredUrl();
      return;
    } else if (!URLUtils.isMail(URLUtils.normaliseMail(url))) {
      url = URLUtils.normalizeUrl(url);
      if (!isUrl(url)) {
        handleInvalidUrl();
        return;
      }
    } else {
      url = URLUtils.normalizeUrl(url);
    }

    if (EditorUtils.hasEntity(getEditorState(), 'LINK')) {
      setEditorState(updateLinkAtSelection(getEditorState(), url));
    } else if (EditorUtils.hasEntity(getEditorState(), 'IMAGE')) {
      setEditorState(updateLinkAtSelection(getEditorState(), url));
    } else {
      setEditorState(EditorUtils.createLinkAtSelection(getEditorState(), url));
    }
    input && input.current && input.current.blur();
    onClose();
  };

  const onSave = (e) => {
    e.stopPropagation();
    e.preventDefault();
    submit();
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      submit();
    } else if (e.key === 'Escape' || e.key === 'Esc') {
      e.preventDefault();
      onClose();
    }
  };

  const onPaste = (e) => {
    e.stopPropagation();
    e.preventDefault();
    let text = e.clipboardData.getData('text/plain');
    if (text && text !== '') {
      text = text.replace(/\n|\r/g, '');
      setValue(text);
      input.current.value = text;
    }
  };

  const { placeholder, editorId } = props;
  const inputStyle = isInvalid
    ? [defaultTheme.input, defaultTheme.inputInvalid]
    : defaultTheme.input;

  let inlineStyle;
  if (props.inStatic && editorId) {
    const toolbar = document.getElementById(`inlineToolbar-${editorId}`);
    inlineStyle = toolbar?.style;
  }

  return (
    <div
      className='add-link-form flex items-center font-body'
      css={
        !props.inStatic
          ? undefined
          : css`
              position: absolute;
              top: ${inlineStyle ? inlineStyle.top : 'calc(100% + 0.25rem)'};
              left: ${inlineStyle ? inlineStyle.left : '50%'};
              transform: translate(-50%, 0);
              border: 1px solid #111;
              background: #333;
              border-radius: 4px;
              box-shadow: 0px 1px 3px 0px rgba(220, 220, 220, 1);
              z-index: 100;
              box-sizing: border-box;
              &:after,
              &:before {
                top: 100%;
                left: 50%;
                border: solid transparent;
                content: ' ';
                height: 0;
                width: 0;
                position: absolute;
                pointer-events: none;
              }
              &:after {
                border-color: rgba(255, 255, 255, 0);
                border-top-color: #333;
                border-width: 4px;
                margin-left: -4px;
              }
              &:before {
                border-color: rgba(221, 221, 221, 0);
                border-top-color: #333;
                border-width: 6px;
                margin-left: -6px;
              }
            `
      }
    >
      <textarea
        wrap='off'
        ref={onRef}
        css={inputStyle}
        onBlur={onClose}
        onChange={onChange}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        placeholder={placeholder}
      />
      {isInvalid && errorMessage && <InputErrorMessage message={errorMessage} />}
      <div css={defaultTheme.buttonWrapper} onMouseDown={onMouseDown}>
        <RemoveBlock {...props} />
        <button css={defaultTheme.button} onClick={onSave}>
          Save
        </button>
      </div>
    </div>
  );
};

AddLinkForm.propTypes = {
  // eslint-disable-next-line react/no-unused-prop-types
  getEditorState: PropTypes.func.isRequired,
  // eslint-disable-next-line react/no-unused-prop-types
  setEditorState: PropTypes.func.isRequired,
  // eslint-disable-next-line react/no-unused-prop-types
  onOverrideContent: PropTypes.func.isRequired,
  theme: PropTypes.object.isRequired,
  placeholder: PropTypes.string,
  // eslint-disable-next-line react/no-unused-prop-types
  validateUrl: PropTypes.func,
  value: PropTypes.string,
};

AddLinkForm.defaultProps = {
  placeholder: 'Enter a URL and press enter',
};

export default AddLinkForm;
